import axios from 'axios';
import _ from 'lodash'
import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import {
  GroupsState,
  GroupConditions,
  Group,
  Student,
  MoveStudentGroupParams,
} from '@/store/groups/types';
import { getPagerFromApiResponse } from '@/store/utils'

const actions: ActionTree<GroupsState, RootState> = {
  /**
   * グループ一覧取得
   * @param {{
   *  conditions: GroupConditions, // グループ検索条件
   *  page: number, // ページ番号
   *  isUpdateState: boolean, // state更新有無 true: 更新する(default), false: 更新しない
   * }}
   */
  fetchGroups: async (
    { commit, dispatch, getters },
    { conditions, page, isUpdateState = true }:
      { conditions?: GroupConditions, page: number, isUpdateState: boolean },
  ) => {
    const { headers, data } = await axios.get('/groups', {
      params: {
        page,
        ..._.pickBy({
          tutor: conditions?.tutor?.id,
          universityCode: conditions?.university?.code,
          campusCode: conditions?.campuses.map(({ code }) => code).join(','),
          dayOfWeek: conditions?.weekdays.join(','),
          period: conditions?.periods.join(','),
          fiscalYear: conditions?.fiscalYearSemester?.fiscalYear,
          semesterCode: conditions?.fiscalYearSemester?.semester?.code,
          removedPagingLimit: conditions?.removedPagingLimit,
          quarter: conditions?.quarter,
        }),
      },
    })

    await Promise.all([
      'fetchMasterUniversities',
      'fetchMasterCampuses',
      'fetchMasterSemesters',
    ].map((key) => dispatch(key)))

    const groups: Group[] = _.map(data, (d): Group => ({
      id: d.id,
      tutor: {
        id: d.tutorId,
        name: d.tutorName,
      },
      university: getters.findUniversityByCode(d.universityCode),
      campus: getters.findCampusByCode(d.campusCode),
      dayOfWeek: d.dayOfWeek,
      fiscalYear: d.fiscalYear,
      period: d.period,
      semester: getters.findSemesterByCode(d.semesterCode),
      courseCode: d.courseId,
      studentsCount: d.studentsCount,
      levelName: d.levelName,
      quarter: d.quarter,
    }))

    if (isUpdateState) {
      commit('fetchedGroups', {
        pager: getPagerFromApiResponse(page, headers),
        groups,
      })
    }

    return groups
  },

  /**
   * グループIDからグループの情報を取得
   * @param {string} groupId グループID
   */
  getGroupById: (
    { commit, dispatch, getters },
    { groupId }: { groupId: string },
  ) => axios.get(`/groups/${groupId}`)
    .then(async ({ data }) => {
      await Promise.all([
        dispatch('getTutor', { tutorId: data.tutorId }),
        ...[
          'fetchMasterUniversities',
          'fetchMasterCampuses',
          'fetchMasterSemesters',
          'fetchMasterLevels',
        ].map((key) => dispatch(key)),
        dispatch('fetchBooths', { conditions: { removedPagingLimit: true } }),
      ])

      const group: Group = {
        id: data.id,
        tutor: {
          id: data.tutorId,
          name: data.tutorName,
        },
        university: getters.findUniversityByCode(data.universityCode),
        campus: getters.findCampusByCode(data.campusCode),
        dayOfWeek: data.dayOfWeek,
        fiscalYear: data.fiscalYear,
        period: data.period,
        semester: getters.findSemesterByCode(data.semesterCode),
        courseCode: data.courseId,
        booth: getters.findBoothByNames({
          university: getters.findUniversityByCode(data.universityCode),
          campus: getters.findCampusByCode(data.campusCode),
          building: data.building,
          room: data.boothRoom,
          number: data.boothNumber,
        }),
        level: getters.findLevelByCode(data.levelCode),
        students: _.map(data.students, (s): Student => ({
          id: s.id,
          name: s.nameEn,
          nameJp: s.name,
          studentNumber: s.studentNumber,
        })),
      }

      commit('gotGroup', group)
    }),

  /**
   * グループ編集
   * @param {{
   *  groupId: string, // 編集対象グループID
   *  group: Group, // 編集後のグループ
   * }}
   */
  editGroup: (
    context,
    { groupId, group }: { groupId: string, group: Group },
  ) => axios.put(`/groups/${groupId}`, {
    dayOfWeek: group.dayOfWeek,
    period: group.period,
    boothId: group.booth?.id,
  }),

  /**
   * 履修者のグループ編成移動
   * @param {{
   *  student: Student, // 履修者
   *  fromGroup: Group, // 移動元グループ
   *  toGroup: Group, // 移動先グループ
   *  spirId: string|undefined, // SPIR申請ID
   * }}
   */
  moveStudentAnotherGroup: (
    context,
    {
      student,
      fromGroup,
      toGroup,
      spirId,
    }: MoveStudentGroupParams,
  ) => axios.put(`/groups/${fromGroup.id}/students/${student.id}`, _.pickBy({
    moveToGroupId: toGroup.id,
    spirApplicationId: spirId,
  })),

  /**
   * 履修者のグループ新規移動
   * @param {{
   *  student: Student, // 履修者
   *  fromGroup: Group, // 移動元グループ
   *  toGroup: Group, // 移動先グループ
   *  spirId: string|undefined, // SPIR申請ID
   * }}
   */
  moveStudentNewGroup: (
    { commit },
    {
      student,
      fromGroup,
      toGroup,
      spirId,
    }: MoveStudentGroupParams,
  ) => axios.post(`/groups/${fromGroup.id}/students/${student.id}`, _.pickBy({
    moveToGroupId: toGroup.id,
    tutorId: toGroup.tutor.id,
    levelCode: toGroup.level?.code,
    dayOfWeek: toGroup.dayOfWeek,
    period: toGroup.period,
    boothId: toGroup.booth?.id,
    spirApplicationId: spirId,
  })).then(({ data }) => {
    commit('createdGroup', data.id)
  }),

  /**
   * 条件にマッチしたグループのグループ情報をチューターと履修者に通知する
   * @param {GroupConditions} conditions 検索条件
   */
  announceGroupInfoToTutorAndStudents: (
    context,
    conditions: GroupConditions,
  ) => axios.post('/groups/announceSchedule', _.pickBy({
    tutor: conditions?.tutor?.id,
    universityCode: conditions?.university?.code,
    campusCode: conditions?.campuses.map(({ code }) => code).join(','),
    dayOfWeek: conditions?.weekdays.join(','),
    period: conditions?.periods.join(','),
    fiscalYear: conditions?.fiscalYearSemester?.fiscalYear,
    semesterCode: conditions?.fiscalYearSemester?.semester?.code,
    quarter: conditions?.quarter,
  })),
};
export default actions;
